import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  useTheme,
  Card,
  CardContent,
  Avatar,
  Button,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Cookies from "js-cookie";
import { AlertError, AlertSuccess } from "../../../component/popupAlert";
import { alpha } from "@mui/material/styles";
import { BsPersonFill } from "react-icons/bs";
import { MdPreview } from "react-icons/md";
import { IoMdInformationCircleOutline } from "react-icons/io";
import ResponsiveAppBar from "../../../component/appbar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { BiSolidDiscount } from "react-icons/bi";
import { FiDownload } from "react-icons/fi";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Styled components for the table
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  "&.MuiTableCell-body": {
    fontSize: 14,
    borderBottom: "1px solid #ddd",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}));

const CouponDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const token = Cookies.get("token");
  const [isLoading, setIsLoading] = useState(true);
  const [couponsList, setCouponsList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [couponSummary, setCouponSummary] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    expired: 0,
  });

  const [searchData, setSearchData] = useState({
    couponCode: "",
    couponName: "",
    status: "",
  });

  // Mock data for demonstration
  const mockCoupons = [
    {
      id: 1,
      couponName: "Summer Sale",
      couponCode: "SUMMER25",
      discountValue: "25",
      couponType: "percentage",
      startDate: "2025-05-01",
      endDate: "2025-08-31",
      isActive: true,
    },
    {
      id: 2,
      couponName: "New User",
      couponCode: "WELCOME",
      discountValue: "500",
      couponType: "fixed",
      startDate: "2025-01-01",
      endDate: "2025-12-31",
      isActive: true,
    },
    {
      id: 3,
      couponName: "Weekend Special",
      couponCode: "WEEKEND10",
      discountValue: "10",
      couponType: "percentage",
      startDate: "2025-04-01",
      endDate: "2025-04-30",
      isActive: false,
    },
    {
      id: 4,
      couponName: "Holiday Discount",
      couponCode: "HOLIDAY20",
      discountValue: "20",
      couponType: "percentage",
      startDate: "2025-12-01",
      endDate: "2025-12-31",
      isActive: true,
    },
    {
      id: 5,
      couponName: "Loyalty Reward",
      couponCode: "LOYAL100",
      discountValue: "100",
      couponType: "fixed",
      startDate: "2025-03-01",
      endDate: "2025-04-15",
      isActive: false,
    },
  ];

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    // Simulate fetching data from API
    const fetchData = async () => {
      try {
        // In a real app, you would make API calls here
        setTimeout(() => {
          setCouponsList(mockCoupons);
          setCouponSummary({
            total: mockCoupons.length,
            active: mockCoupons.filter((c) => c.isActive).length,
            inactive: mockCoupons.filter((c) => !c.isActive).length,
            expired: mockCoupons.filter((c) => new Date(c.endDate) < new Date())
              .length,
          });
          setIsLoading(false);
        }, 1000);
      } catch (error) {
        console.error("Error fetching data:", error);
        AlertError("Error", "Failed to fetch coupon data");
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate, token]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    // In a real app, this would call an API with the search parameters
    console.log("Searching with parameters:", searchData);
  };

  const handleResetSearch = () => {
    setSearchData({
      couponCode: "",
      couponName: "",
      status: "",
    });
  };

  const handleSearchInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const exportToExcel = () => {
    try {
      const formattedData = couponsList.map((coupon, index) => ({
        'ลำดับ': index + 1,
        'รหัสคูปอง': coupon.couponCode,
        'ชื่อคูปอง': coupon.couponName,
        'ประเภท': coupon.couponType === "percentage" ? "เปอร์เซ็นต์" : "จำนวนเงิน",
        'มูลค่า': coupon.discountValue + (coupon.couponType === "percentage" ? "%" : " บาท"),
        'วันที่เริ่มต้น': coupon.startDate,
        'วันที่สิ้นสุด': coupon.endDate,
        'สถานะ': coupon.isActive ? "เปิดใช้งาน" : "ปิดใช้งาน"
      }));
  
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
  
      // Set column widths
      worksheet['!cols'] = [
        { wch: 7 }, { wch: 15 }, { wch: 25 }, { wch: 15 },
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
      ];
  
      // Add header style (background color)
      const headerCellRange = XLSX.utils.decode_range(worksheet['!ref']);
      for (let C = headerCellRange.s.c; C <= headerCellRange.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ c: C, r: 0 }); // Row 0
        if (!worksheet[cellAddress]) continue;
  
        worksheet[cellAddress].s = {
          fill: {
            patternType: "solid",
            fgColor: { rgb: "D9E1F2" }, // Light blue
          },
          font: {
            bold: true,
            color: { rgb: "000000" }
          },
          alignment: {
            horizontal: "center",
            vertical: "center"
          }
        };
      }
  
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "รายการคูปองส่วนลด");
  
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
  
      saveAs(data, `รายการคูปองส่วนลด_${new Date().toLocaleDateString('th-TH')}.xlsx`);
      AlertSuccess("Success", "ส่งออกข้อมูลเป็น Excel สำเร็จ");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      AlertError("Error", "ไม่สามารถส่งออกข้อมูลเป็น Excel ได้");
    }
  };

  // Status card component for summary statistics
  const StatusCard = ({ title, count, icon, color, onClick }) => {
    return (
      <Card
        sx={{
          backgroundColor: "white",
          height: "100%",
          borderRadius: 4,
          position: "relative",
          overflow: "hidden",
          transition: "transform 0.3s, box-shadow 0.3s",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: theme.shadows[10],
            cursor: "pointer",
          },
        }}
        onClick={onClick}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            background: `linear-gradient(45deg, ${alpha(color, 0.2)}, ${alpha(
              color,
              0.1
            )})`,
            zIndex: 0,
          }}
        />
        <CardContent
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Avatar
                sx={{
                  bgcolor: color,
                  width: 52,
                  height: 52,
                  boxShadow: `0 4px 20px ${alpha(color, 0.5)}`,
                }}
              >
                {icon}
              </Avatar>
              <Typography
                variant="h6"
                marginTop={"18px"}
                sx={{
                  fontWeight: "medium",
                  color: theme.palette.text.secondary,
                }}
              >
                {title}
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: "bold",
                color: theme.palette.text.primary,
              }}
            >
              {count}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <ResponsiveAppBar />
      <Box
        sx={{ display: "flex", bgcolor: "white", margin: 4, paddingBottom: 4 }}
      >
        {/* Main content area */}
        <Box
          sx={{
            flexGrow: 1,
            width: "100%",
          }}
        >
          <Typography variant="h4" gutterBottom>
            Coupon Dashboard
          </Typography>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <StatusCard
                title="คูปองทั้งหมด"
                count={couponSummary.total}
                icon={<BiSolidDiscount />}
                color="#3f51b5"
                onClick={() => console.log("All coupons clicked")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatusCard
                title="คูปองที่ใช้งานได้"
                count={couponSummary.active}
                icon={<BiSolidDiscount />}
                color="#4caf50"
                onClick={() => console.log("Active coupons clicked")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatusCard
                title="คูปองที่ถูกปิด"
                count={couponSummary.inactive}
                icon={<BiSolidDiscount />}
                color="#ff9800"
                onClick={() => console.log("Inactive coupons clicked")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatusCard
                title="คูปองที่หมดอายุ"
                count={couponSummary.expired}
                icon={<BiSolidDiscount />}
                color="#f44336"
                onClick={() => console.log("Expired coupons clicked")}
              />
            </Grid>
          </Grid>

          {/* Search Section */}
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Card sx={{ padding: 3, marginTop: 1, marginBottom: 3 }}>
              <Typography variant="h6" gutterBottom>
                ค้นหารายการส่วนลด
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ marginBottom: 1 }}>รหัสคูปอง</Typography>
                  <TextField
                    name="couponCode"
                    label="รหัสคูปอง"
                    value={searchData.couponCode}
                    onChange={handleSearchInputChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ marginBottom: 1 }}>ชื่อคูปอง</Typography>
                  <TextField
                    name="couponName"
                    label="ชื่อคูปอง"
                    value={searchData.couponName}
                    onChange={handleSearchInputChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ marginBottom: 1 }}>สถานะ</Typography>
                  <TextField
                    select
                    name="status"
                    label="สถานะ"
                    value={searchData.status}
                    onChange={handleSearchInputChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">ทั้งหมด</MenuItem>
                    <MenuItem value="active">ใช้งานได้</MenuItem>
                    <MenuItem value="inactive">ไม่ใช้งาน</MenuItem>
                    <MenuItem value="expired">หมดอายุ</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ marginRight: 2, width: "120px" }}
                  onClick={handleResetSearch}
                >
                  ล้างค่า
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ width: "120px" }}
                  onClick={handleSearch}
                >
                  ค้นหา
                </Button>
              </Box>
            </Card>
          </LocalizationProvider>

          {/* Coupon List Table */}
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <Typography variant="h6">รายการคูปองส่วนลด</Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<FiDownload />}
                  onClick={exportToExcel}
                >
                  ส่งออก Excel
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>ลำดับ</StyledTableCell>
                      <StyledTableCell>รหัสคูปอง</StyledTableCell>
                      <StyledTableCell>ชื่อคูปอง</StyledTableCell>
                      <StyledTableCell>ประเภท</StyledTableCell>
                      <StyledTableCell>มูลค่า</StyledTableCell>
                      <StyledTableCell>วันที่เริ่มต้น</StyledTableCell>
                      <StyledTableCell>วันที่สิ้นสุด</StyledTableCell>
                      <StyledTableCell>สถานะ</StyledTableCell>
                      <StyledTableCell>จัดการ</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {couponsList
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((coupon, index) => (
                        <StyledTableRow key={coupon.id}>
                          <TableCell>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell>{coupon.couponCode}</TableCell>
                          <TableCell>{coupon.couponName}</TableCell>
                          <TableCell>
                            {coupon.couponType === "percentage"
                              ? "เปอร์เซ็นต์"
                              : "จำนวนเงิน"}
                          </TableCell>
                          <TableCell>
                            {coupon.discountValue}
                            {coupon.couponType === "percentage" ? "%" : " บาท"}
                          </TableCell>
                          <TableCell>{coupon.startDate}</TableCell>
                          <TableCell>{coupon.endDate}</TableCell>
                          <TableCell>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={coupon.isActive}
                                  size="small"
                                />
                              }
                              label={
                                coupon.isActive ? "เปิดใช้งาน" : "ปิดใช้งาน"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/thewonderblueadmin_coupon/view/${coupon.id}`
                                )
                              }
                            >
                              <MdPreview fontSize={"24px"} color="gray" />
                            </Button>
                          </TableCell>
                        </StyledTableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={couponsList.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="จำนวนรายการต่อหน้า"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} จาก ${count}`
                }
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default CouponDashboard;